import { useMemo, useCallback } from 'react';
import type {
    TeamMember,
    TeamMemberDetail,
    ProfessionalMessageProps,
} from './types';

/**
 * Normalizes category name with special handling for Interior Designer categories
 */
export const normalizeCategory = (category: string): string => {
    if (!category) return '';

    const lowerCategory = category.toLowerCase();

    if (
        lowerCategory === 'interior designer' ||
        lowerCategory === 'interiordesigners'
    ) {
        return 'interiordesigners';
    }

    return lowerCategory;
};

/**
 * Formats category name for display purposes
 */
export const formatCategoryName = (category: string): string => {
    return category === 'InteriorDesigners' ? 'Interior Designers' : category;
};

/**
 * Capitalizes the first letter of a string
 */
export const capitalize = (str: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Creates an EnquiryProfessional object from ProfessionalMessageProps
 * @param professionalProps - The properties of the professional to convert, or null
 * @returns An EnquiryProfessional object for use with the enquiry modal
 */
export const createEnquiryProfessional = (
    professionalProps: ProfessionalMessageProps | null,
) => {
    if (!professionalProps) {
        return {
            ID: 0,
            Title: '',
            Link: '',
            CompanyLogo: '',
            LogoBackgroundColor: '',
            ArchiproEditorialAccount: false,
            CustomEnquiryMessage: '',
            AvailableForWork: false,
            LibraryLocations: [],
            branchOptions: [],
            ctaLabel: '',
        };
    }

    return {
        ID: professionalProps.ProfessionalID,
        Title: professionalProps.Title,
        Link: professionalProps.Link || '',
        CompanyLogo: professionalProps.CompanyLogo || '',
        LogoBackgroundColor: professionalProps.LogoBackgroundColor || '',
        ArchiproEditorialAccount:
            professionalProps.ArchiproEditorialAccount === 'true',
        CustomEnquiryMessage: professionalProps.CustomEnquiryMessage || '',
        AvailableForWork: professionalProps.AvailableForWork || false,
        LibraryLocations: [],
        branchOptions: [],
        ctaLabel: professionalProps.CtaLabel || '',
    };
};

/**
 * List of team categories in display order
 */
const TEAM_CATEGORIES: string[] = [
    'Architects',
    'Builders',
    'InteriorDesigners',
    'Engineers',
    'Landscapers',
    'Joinery',
];

/**
 * Custom hook that processes project team data for display and navigation
 */
export const useProcessedTeamData = (projectTeamMembers: TeamMember[]) => {
    // Filter and order all team members by category
    const allTeamMembers = useMemo(() => {
        const orderedMembers: TeamMemberDetail[] = [];

        TEAM_CATEGORIES.forEach((categoryName) => {
            const categoryData = projectTeamMembers.find(
                (category) =>
                    normalizeCategory(category.Category) ===
                    normalizeCategory(categoryName),
            );

            if (categoryData) {
                const categoryMembers = categoryData.TeamMembers.filter(
                    (member: { IsPublic?: boolean }) =>
                        member.IsPublic !== false,
                ).map(
                    (member: {
                        ID: number;
                        Name: string;
                        JobTitle: string;
                        Thumbnail: string;
                        Summary?: string;
                        ProfessionalID: number;
                        ProfessionalName: string;
                        IsPublic?: boolean;
                    }) => ({
                        ...member,
                        Category: categoryData.Category,
                        LibraryLocations: [],
                    }),
                );

                orderedMembers.push(...categoryMembers);
            }
        });

        return orderedMembers;
    }, [projectTeamMembers]);

    // Group team members by their categories
    const categoryTeamMembers = useMemo(() => {
        const groupedMembers: Record<string, TeamMemberDetail[]> = {};

        allTeamMembers.forEach((member: TeamMemberDetail) => {
            if (member.Category) {
                const categoryKey = normalizeCategory(member.Category);

                if (!groupedMembers[categoryKey]) {
                    groupedMembers[categoryKey] = [];
                }

                groupedMembers[categoryKey]!.push(member);
            }
        });

        return groupedMembers;
    }, [allTeamMembers]);

    // Get list of available categories in sorted order
    const availableCategories = useMemo(
        () =>
            Object.keys(categoryTeamMembers).map((normalizedKey) => {
                const category = projectTeamMembers.find(
                    (c) => normalizeCategory(c.Category) === normalizedKey,
                );
                return category ? category.Category : capitalize(normalizedKey);
            }),
        [categoryTeamMembers, projectTeamMembers],
    );

    return {
        allTeamMembers,
        categoryTeamMembers,
        availableCategories,
        // Helper functions
        getCategoryMembers: (activeTab: string) => {
            if (!activeTab) return [];
            const normalizedKey = normalizeCategory(activeTab);
            return categoryTeamMembers[normalizedKey] || [];
        },
        findCategoryForMember: (member: TeamMemberDetail | null) => {
            if (!member?.Category) return null;
            const normalizedCategory = normalizeCategory(member.Category);
            return projectTeamMembers.find(
                (category) =>
                    normalizeCategory(category.Category) === normalizedCategory,
            );
        },
    };
};

/**
 * Custom hook that handles navigation between team members within and across categories
 */
export const useTeamNavigation = (
    activeTab: string,
    availableCategories: string[],
    categoryTeamMembers: Record<string, TeamMemberDetail[]>,
    setActiveTab: (tab: string) => void,
    activeCategoryIndex: number,
    setActiveCategoryIndex: (index: number) => void,
) => {
    // Find the next or previous valid category
    const findNextValidCategory = useCallback(
        (currentIndex: number, direction: 'prev' | 'next') => {
            if (availableCategories.length <= 1) return null;

            const nextIndex =
                direction === 'next'
                    ? (currentIndex + 1) % availableCategories.length
                    : (currentIndex - 1 + availableCategories.length) %
                      availableCategories.length;

            const categoryName = availableCategories[nextIndex] || '';

            return {
                categoryIndex: nextIndex,
                categoryName,
                normalizedKey: normalizeCategory(categoryName),
            };
        },
        [availableCategories],
    );

    // Handle navigation between team members
    const handleNavigation = useCallback(
        (direction: 'prev' | 'next', allTeamMembersLength: number) => {
            if (allTeamMembersLength <= 1) return;

            // Get current category team members
            const normalizedKey = normalizeCategory(activeTab);
            const currentMembers = categoryTeamMembers[normalizedKey] || [];

            // Check if we can navigate within the current category
            if (
                direction === 'next' &&
                activeCategoryIndex < currentMembers.length - 1
            ) {
                setActiveCategoryIndex(activeCategoryIndex + 1);
                return;
            } else if (direction === 'prev' && activeCategoryIndex > 0) {
                setActiveCategoryIndex(activeCategoryIndex - 1);
                return;
            }

            // Navigate between categories if needed
            const currentCategoryIndex = availableCategories.findIndex(
                (category) =>
                    normalizeCategory(category) ===
                    normalizeCategory(activeTab),
            );

            if (currentCategoryIndex === -1) return;

            // Find the next valid category
            const nextCategory = findNextValidCategory(
                currentCategoryIndex,
                direction,
            );

            if (nextCategory) {
                // Get members of the next/prev category
                const members =
                    categoryTeamMembers[nextCategory.normalizedKey] || [];

                setActiveTab(nextCategory.categoryName);

                // When going backwards, ALWAYS navigate to the LAST member of the previous category
                if (direction === 'prev') {
                    const lastIndex = Math.max(0, members.length - 1);
                    setActiveCategoryIndex(lastIndex);
                } else {
                    // When going forwards, always navigate to the FIRST member of the next category
                    setActiveCategoryIndex(0);
                }
            }
        },
        [
            activeTab,
            availableCategories,
            categoryTeamMembers,
            activeCategoryIndex,
            findNextValidCategory,
            setActiveTab,
            setActiveCategoryIndex,
        ],
    );

    return {
        handleNavigation,
        findNextValidCategory,
    };
};
